import { useState } from 'react';
import { OrderDto } from '../../types/opsBriefing';
import { pickCoachPreview } from './coachPreview';
import CoachOrderCard from './CoachOrderCard';
import { COACH_PUSH_TITLE, DRAFT_BY_APP_NOTICE } from './CoachOrderPreview';

/**
 * 강사용 앱 화면 미리보기 (모바일).
 *
 * 지시가 강사에게 어떻게 보이는지 대표님이 확인할 수 있어야, 무엇을 보낼지 감이 잡힌다.
 * 실제 강사 앱은 별도지만 UI 규격을 여기서 확정한다.
 *
 * ─────────────────────────────────────────────────────────────
 * 내용은 **실제로 나간 지시**에서 뽑습니다
 * ─────────────────────────────────────────────────────────────
 * 예전에는 목 지시로 초기화해서, 없는 센터 이름('헬스보이짐 천천점')과
 * 없는 회원 이름이 떴습니다. "우리 강사에게 어떻게 보이나"를 확인하러 연 화면에
 * 남의 데이터가 뜨면 확인이 아니라 오해가 됩니다.
 *
 * 톤: 숙제 검사처럼 느껴지면 강사가 형식적으로 완료 처리한다.
 *     그래서 완료 시 가벼운 축하를 준다.
 *
 * 다만 이 안에서 누르는 버튼은 **아무것도 저장하지 않습니다.** 껍데기를 보는 자리입니다.
 * 그 사실을 화면에 적어 둡니다 — 여기서 완료 보고를 하면 강사가 한 것으로
 * 착각하게 두면 안 됩니다.
 */

const PHONE_W = 340;

type Props = {
    /** 서버에서 받아 온 지시 목록. 여기서 한 강사분을 골라 보여준다 */
    orders: OrderDto[];
    /** 아직 불러오는 중 — '지시가 없다'와 구분해야 한다 */
    loading?: boolean;
    onClose: () => void;
};

const CoachTaskMobilePreview = ({ orders, loading = false, onClose }: Props) => {
    const preview = pickCoachPreview(orders);

    /*
     * 지시 목록을 state 로 복사해 두지 않습니다.
     * 팝업을 연 뒤에 조회가 끝나면 state 는 빈 배열로 굳어 버려서
     * 지시가 있는데도 "아직 나간 지시가 없습니다"가 뜹니다.
     *
     * 여기서 쓰는 로컬 state 는 **미리보기 안에서 흉내 낸 완료 보고**뿐입니다.
     * 어디에도 저장되지 않습니다.
     */
    const [mockReports, setMockReports] = useState<Record<string, { text: string; at: string }>>({});
    const [sheetFor, setSheetFor] = useState<string | null>(null);
    const [report, setReport] = useState('');
    const [celebrate, setCelebrate] = useState(false);

    const tasks: OrderDto[] = (preview?.tasks ?? []).map((t) => {
        const mine = mockReports[t.orderId];
        if (!mine) return t;
        return {
            ...t,
            status: 'DONE' as const,
            doneAt: mine.at,
            doneBy: 'COACH' as const,
            coachReport: mine.text,
        };
    });

    const submit = () => {
        if (!sheetFor || !report.trim()) return;
        setMockReports((prev) => ({
            ...prev,
            [sheetFor]: { text: report.trim(), at: new Date().toISOString() },
        }));
        setSheetFor(null);
        setReport('');
        setCelebrate(true);
        window.setTimeout(() => setCelebrate(false), 2200);
    };

    const pending = tasks.filter((t) => t.status !== 'DONE');

    return (
        <div
            className="fixed inset-0 flex items-center justify-center"
            style={{ background: 'rgba(12,22,40,0.6)', zIndex: 2200, padding: 24 }}
            onClick={onClose}
        >
            <div className="flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
                <span style={{ color: '#FFFFFF', fontSize: 14, marginBottom: 4 }}>
                    {preview
                        ? `${preview.coachName} 강사 앱에서는 이렇게 보입니다`
                        : '강사 앱에서는 이렇게 보입니다'}
                </span>
                {/* 여기서 누른 것은 저장되지 않는다. 안 적어 두면 완료 보고를 한 줄 안다 */}
                <span style={{ color: 'rgba(255,255,255,0.62)', fontSize: 12.5, marginBottom: 12 }}>
                    미리보기입니다 · 여기서 누른 내용은 저장되지 않습니다
                </span>

                {/* 폰 프레임 */}
                <div
                    className="relative flex flex-col overflow-hidden"
                    style={{
                        width: PHONE_W,
                        height: 680,
                        background: '#F4F6F9',
                        borderRadius: 34,
                        border: '9px solid #10182A',
                        boxShadow: '0 30px 70px rgba(0,0,0,0.4)',
                    }}
                >
                    {/* 앱 헤더 */}
                    <div
                        className="flex-none"
                        style={{
                            background: 'linear-gradient(135deg, #1A305D 0%, #2B5183 100%)',
                            padding: '26px 20px 20px',
                        }}
                    >
                        {/*
                          센터 이름을 목에서 가져다 쓰고 있었다('헬스보이짐 천천점').
                          브리핑 응답에 센터 이름이 없어서, 대신 실제 지시의 강사 이름을 쓴다.
                          모르는 값을 그럴듯한 이름으로 채우지 않는다.
                        */}
                        <span style={{ fontSize: 12, color: '#9DB4D8' }}>
                            {preview ? `${preview.coachName} 강사` : '강사'}
                        </span>
                        <div style={{ marginTop: 6 }}>
                            <span style={{ fontSize: 21, fontWeight: 800, color: '#FFFFFF', letterSpacing: '-0.02em' }}>
                                요청받은 일{' '}
                                <span style={{ color: '#FFD877' }}>{pending.length}건</span>
                            </span>
                        </div>
                        {/*
                          앱은 원장을 「관리자님」이라고 부릅니다 (ModalCoachOpsOrder ·
                          푸시 템플릿 COA_ORDER). 여기만 「원장님」이면 같은 화면을
                          두 이름으로 부르는 셈이라 미리보기가 어긋납니다.
                        */}
                        <span style={{ fontSize: 12.5, color: '#B7C7E0', marginTop: 6, display: 'block' }}>
                            {COACH_PUSH_TITLE}
                        </span>
                    </div>

                    {/* 태스크 목록 */}
                    <div className="flex flex-1 flex-col overflow-y-auto" style={{ padding: 14, gap: 12 }}>
                        {/* 불러오는 중과 '지시가 없다'를 구분한다. 뭉개면 없는 것으로 읽힌다 */}
                        {loading && tasks.length === 0 && (
                            <div
                                className="flex flex-1 flex-col items-center justify-center"
                                style={{ padding: '40px 20px', textAlign: 'center' }}
                            >
                                <span style={{ fontSize: 14, color: '#93A0AE' }}>불러오는 중입니다…</span>
                            </div>
                        )}
                        {!loading && tasks.length === 0 && (
                            <div
                                className="flex flex-1 flex-col items-center justify-center"
                                style={{ padding: '40px 20px', textAlign: 'center' }}
                            >
                                <span style={{ fontSize: 30 }}>📭</span>
                                <span style={{ fontSize: 15, fontWeight: 700, color: '#5B6B7F', marginTop: 12 }}>
                                    아직 나간 지시가 없습니다
                                </span>
                                <span style={{ fontSize: 13, color: '#93A0AE', marginTop: 6, lineHeight: '19px' }}>
                                    브리핑 카드에서 지시를 보내시면
                                    <br />
                                    강사 앱에 이렇게 쌓입니다
                                </span>
                            </div>
                        )}
                        {/*
                          카드는 **보내기 전 미리보기와 같은 것**을 씁니다 (CoachOrderCard).
                          두 곳이 각자 그리면 갈립니다 — 실제로 갈려 있었습니다.
                          여기가 「원장님 메모」였을 때 실제 앱은 「관리자님 메모」였고,
                          글의 순서도 달랐습니다.

                          ⚠️ 근거(reason·facts)는 이 미리보기에 뜨지 않습니다. 강사 앱에는
                             뜹니다(서버가 생성 시점에 굳혀 둡니다). 원장 화면의 지시 목록
                             응답(OrderDto)에 그 값이 없어서 여기서는 그릴 수가 없습니다.
                             지어내지 않고 비워 둡니다. 보내기 전 미리보기에는 뜹니다 —
                             그때는 브리핑 항목이 손에 있기 때문입니다.
                        */}
                        {tasks.map((t) => {
                            const isDone = t.status === 'DONE';
                            return (
                                <CoachOrderCard
                                    key={t.orderId}
                                    memberName={t.memberName}
                                    task={t.task}
                                    ownerNote={t.ownerNote}
                                    memberDraft={t.memberDraft}
                                    dimmed={isDone}
                                    badge={
                                        isDone ? (
                                            <span
                                                className="rounded-[5px]"
                                                style={{ background: '#E3F1EC', color: '#00795F', fontSize: 11, fontWeight: 800, padding: '3px 7px' }}
                                            >
                                                완료
                                            </span>
                                        ) : (
                                            <span
                                                className="rounded-[5px]"
                                                style={{ background: '#FEF3C7', color: '#B45309', fontSize: 11, fontWeight: 800, padding: '3px 7px' }}
                                            >
                                                요청
                                            </span>
                                        )
                                    }
                                    /*
                                      원장이 문구를 안 남긴 지시. 실제 앱은 회원 이름과 근거로
                                      첫 마디를 만들어 채웁니다(rappoApp features/opsOrder/draft.ts).
                                      그 문장을 여기서 흉내 내면 앱이 만든 문장과 갈리므로,
                                      만들어진다는 사실만 적습니다.
                                    */
                                    draftFallback={
                                        <span style={{ fontSize: 12, color: '#7C8794', lineHeight: '18px' }}>
                                            {DRAFT_BY_APP_NOTICE}
                                        </span>
                                    }
                                    footer={
                                        isDone && t.coachReport ? (
                                            <div
                                                className="rounded-[10px]"
                                                style={{ marginTop: 13, background: '#F0F7F4', padding: '10px 12px' }}
                                            >
                                                <span style={{ fontSize: 11.5, color: '#00795F', fontWeight: 700 }}>
                                                    내가 보고한 내용
                                                </span>
                                                <p style={{ fontSize: 13.5, color: '#2C3A4B', marginTop: 4 }}>
                                                    {t.coachReport}
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="flex" style={{ gap: 7, marginTop: 13 }}>
                                                <button
                                                    type="button"
                                                    aria-label={`${t.memberName} 회원에게 전화 걸기`}
                                                    className="rounded-[9px]"
                                                    style={{
                                                        flex: 1,
                                                        height: 42,
                                                        background: '#FFFFFF',
                                                        border: '1px solid #DCE4EE',
                                                        color: '#425063',
                                                        fontSize: 14,
                                                        fontWeight: 600,
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    전화 걸기
                                                </button>
                                                <button
                                                    type="button"
                                                    aria-label={`${t.memberName} 회원 건 완료 보고하기`}
                                                    onClick={() => {
                                                        setSheetFor(t.orderId);
                                                        setReport('');
                                                    }}
                                                    className="rounded-[9px]"
                                                    style={{
                                                        flex: 1.3,
                                                        height: 42,
                                                        background: '#1A305D',
                                                        border: 'none',
                                                        color: '#FFFFFF',
                                                        fontSize: 14,
                                                        fontWeight: 700,
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    완료했어요
                                                </button>
                                            </div>
                                        )
                                    }
                                />
                            );
                        })}
                    </div>

                    {/* 바텀시트 */}
                    {sheetFor && (
                        <div
                            className="absolute inset-0 flex flex-col justify-end"
                            style={{ background: 'rgba(10,18,32,0.45)' }}
                            onClick={() => setSheetFor(null)}
                        >
                            <div
                                style={{
                                    background: '#FFFFFF',
                                    borderTopLeftRadius: 20,
                                    borderTopRightRadius: 20,
                                    padding: '18px 18px 22px',
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div
                                    style={{
                                        width: 38,
                                        height: 4,
                                        borderRadius: 2,
                                        background: '#DCE4EE',
                                        margin: '0 auto 16px',
                                    }}
                                />
                                <span style={{ fontSize: 17, fontWeight: 700, color: '#16202E' }}>
                                    결과를 알려주세요
                                </span>
                                <span style={{ fontSize: 13, color: '#7C8794', marginTop: 5, display: 'block' }}>
                                    한 줄이면 충분해요. 원장님께 그대로 전달됩니다.
                                </span>
                                <span style={{ fontSize: 12, color: '#B4BDC9', marginTop: 3, display: 'block' }}>
                                    (미리보기라 실제로 저장되지 않습니다)
                                </span>
                                <textarea
                                    value={report}
                                    onChange={(e) => setReport(e.target.value)}
                                    rows={3}
                                    autoFocus
                                    placeholder="예) 금일 통화 완료, 다음 수업 때 연장하기로 함"
                                    style={{
                                        width: '100%',
                                        marginTop: 12,
                                        border: '1px solid #DCE4EE',
                                        borderRadius: 11,
                                        padding: '12px 13px',
                                        fontSize: 14,
                                        color: '#2C3A4B',
                                        resize: 'none',
                                        fontFamily: 'inherit',
                                        outline: 'none',
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={submit}
                                    aria-label="완료 보고 전송"
                                    disabled={!report.trim()}
                                    className="rounded-[11px]"
                                    style={{
                                        width: '100%',
                                        height: 50,
                                        marginTop: 12,
                                        background: report.trim() ? '#1A305D' : '#DDE3EC',
                                        border: 'none',
                                        color: '#FFFFFF',
                                        fontSize: 16,
                                        fontWeight: 700,
                                        cursor: report.trim() ? 'pointer' : 'not-allowed',
                                    }}
                                >
                                    전송
                                </button>
                            </div>
                        </div>
                    )}

                    {/* 완료 축하 */}
                    {celebrate && (
                        <div
                            className="absolute inset-0 flex items-center justify-center"
                            style={{ background: 'rgba(10,18,32,0.5)' }}
                        >
                            <div
                                className="flex flex-col items-center rounded-[18px]"
                                style={{ background: '#FFFFFF', padding: '28px 30px', width: 250 }}
                            >
                                <span style={{ fontSize: 40 }}>🎉</span>
                                <span style={{ fontSize: 17, fontWeight: 800, color: '#16202E', marginTop: 10 }}>
                                    미션 클리어!
                                </span>
                                <span
                                    style={{
                                        fontSize: 13.5,
                                        color: '#7C8794',
                                        marginTop: 6,
                                        textAlign: 'center',
                                        lineHeight: '19px',
                                    }}
                                >
                                    강사가 보고하면 원장님 화면에
                                    <br />
                                    이렇게 올라옵니다
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                <button
                    type="button"
                    onClick={onClose}
                    className="rounded-[10px]"
                    style={{
                        marginTop: 16,
                        height: 42,
                        padding: '0 22px',
                        background: 'rgba(255,255,255,0.14)',
                        border: '1px solid rgba(255,255,255,0.3)',
                        color: '#FFFFFF',
                        fontSize: 15,
                        cursor: 'pointer',
                    }}
                >
                    닫기
                </button>
            </div>
        </div>
    );
};

export default CoachTaskMobilePreview;
